import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { DashboardJob, reconcileJobs, toDashboardJobs } from '../jobModel';

const POLL_INTERVAL_MS = 5000;

/** Loads export, label and mapping jobs and polls them; unchanged jobs keep their identity between polls. */
export function useDashboardJobs(pollIntervalMs = POLL_INTERVAL_MS) {
  const [jobs, setJobs] = useState<DashboardJob[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const inFlight = useRef<Promise<void> | null>(null);

  // Concurrent callers (poll tick + manual refresh) share one request instead of racing.
  const fetchJobs = useCallback((): Promise<void> => {
    if (!inFlight.current) {
      inFlight.current = Promise.all([
        apiService.getAllJobs(),
        apiService.getAllLabelJobs(),
        apiService.getMappingDocumentJobs(),
      ])
        .then(([exportJobs, labelJobs, mappingJobs]) => {
          const next = toDashboardJobs(
            Array.isArray(exportJobs) ? exportJobs : [],
            Array.isArray(labelJobs) ? labelJobs : [],
            Array.isArray(mappingJobs) ? mappingJobs : [],
          );
          setJobs((prev) => reconcileJobs(prev, next));
        })
        // Keep the last good data on a failed poll rather than blanking the table.
        .catch((error) => console.error('Failed to fetch jobs:', error))
        .finally(() => {
          inFlight.current = null;
          setLoaded(true);
        });
    }
    return inFlight.current;
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchJobs();
    setRefreshing(false);
  }, [fetchJobs]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, pollIntervalMs);
    return () => clearInterval(interval);
  }, [fetchJobs, pollIntervalMs]);

  return { jobs, loaded, refreshing, refresh, refetch: fetchJobs };
}
